import { useRef } from 'react';
import { m, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import type React from 'react';

import { Container } from '~/components/ui/Container';
import { Reveal } from '~/components/ui/Reveal';
import { SectionHeader } from '~/components/ui/SectionHeader';
import { cn } from '~/lib/cn';
import { useContent } from '~/hooks/useContent';

/**
 * ProcessTimeline — 6-step horizontal timeline on desktop, vertical on mobile.
 * bg-deep-panel to create visual separation from the surrounding void-navy sections.
 * Desktop: a scroll-linked scaleX fill line animates as the user scrolls through the section.
 */
function ProcessTimeline(): React.ReactElement {
  const content = useContent();
  const { process } = content;
  const prefersReducedMotion = useReducedMotion();

  // Scroll progress for the fill line (desktop only)
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start center', 'end center'],
  });
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      ref={sectionRef}
      id="process"
      className={cn('bg-[var(--color-deep-panel)]', 'py-24 md:py-32', 'scroll-anchor')}
      aria-labelledby="process-heading"
    >
      <Container>
        <SectionHeader
          eyebrow={process.eyebrow}
          heading={process.heading}
          align="left"
          id="process-heading"
          className="mb-16 md:mb-20 max-w-2xl"
        />

        {/* ── Desktop timeline (md+): horizontal grid with connecting line ── */}
        <ol className="hidden md:grid md:grid-cols-6 gap-4 relative" aria-label={process.heading}>
          {/* Static background track — full width at low opacity */}
          <li
            aria-hidden="true"
            className={cn(
              'absolute top-[19px] left-0 right-0 h-px',
              'bg-gradient-to-r from-transparent via-[var(--color-cyan-signal)] to-transparent',
              'opacity-15',
              'z-0'
            )}
          />

          {/* Scroll-linked progress fill overlay — grows from left to right */}
          {!prefersReducedMotion && (
            <li
              aria-hidden="true"
              className="absolute top-[19px] left-0 right-0 h-px z-0 overflow-hidden"
              style={{ pointerEvents: 'none' }}
            >
              <m.span
                className="absolute inset-0 bg-[var(--color-cyan-signal)] opacity-60"
                style={{ scaleX, transformOrigin: 'left' }}
              />
            </li>
          )}

          {process.steps.map((step, i) => (
            // as="li" ensures the motion wrapper renders as <li> directly,
            // keeping ol > li DOM structure valid (no div between ol and li).
            <Reveal
              key={step.step}
              as="li"
              delay={i * 80}
              className="relative z-10 flex flex-col items-center text-center gap-4"
            >
              {/* Numbered circle */}
              <div
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                  'border-2 border-[var(--color-cyan-signal)]',
                  'bg-[var(--color-deep-panel)]',
                  'font-display font-bold text-sm tracking-wider',
                  'text-[var(--color-cyan-signal)]'
                )}
                aria-hidden="true"
              >
                {step.step}
              </div>

              {/* Title */}
              <h3
                className={cn(
                  'font-head font-semibold text-base tracking-[0.05em]',
                  'text-[var(--color-frost-white)]'
                )}
              >
                {step.title}
              </h3>

              {/* Body */}
              <p
                className={cn(
                  'font-body font-light text-xs leading-[1.8]',
                  'text-[var(--color-steel-mist)]'
                )}
              >
                {step.body}
              </p>
            </Reveal>
          ))}
        </ol>

        {/* ── Mobile timeline: vertical stack with left connecting line ── */}
        <ol className="flex flex-col gap-0 md:hidden" aria-label={process.heading}>
          {process.steps.map((step, i) => (
            // as="li" keeps ol > li structure valid on mobile too
            <Reveal
              key={step.step}
              as="li"
              delay={i * 80}
              className="flex gap-5 pb-10 last:pb-0 relative"
            >
              {/* Vertical connecting line */}
              <div
                className={cn(
                  'absolute top-10 left-5 bottom-0 w-px',
                  'bg-gradient-to-b from-[var(--color-cyan-signal)] to-transparent',
                  'opacity-30',
                  'last:hidden'
                )}
                aria-hidden="true"
              />

              {/* Numbered circle */}
              <div
                className={cn(
                  'relative z-10 flex-shrink-0',
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  'border-2 border-[var(--color-cyan-signal)]',
                  'bg-[var(--color-deep-panel)]',
                  'font-display font-bold text-sm tracking-wider',
                  'text-[var(--color-cyan-signal)]'
                )}
                aria-hidden="true"
              >
                {step.step}
              </div>

              {/* Content */}
              <div className="flex flex-col gap-1.5 pt-1.5">
                <h3
                  className={cn(
                    'font-head font-semibold text-lg tracking-[0.05em]',
                    'text-[var(--color-frost-white)]'
                  )}
                >
                  {step.title}
                </h3>
                <p
                  className={cn(
                    'font-body font-light text-sm leading-[1.8]',
                    'text-[var(--color-steel-mist)]'
                  )}
                >
                  {step.body}
                </p>
              </div>
            </Reveal>
          ))}
        </ol>
      </Container>
    </section>
  );
}

export { ProcessTimeline };
export default ProcessTimeline;
